/**
 * useAirportDetails — fetches comprehensive airport data including
 * NASR airport info, runways, communications, CIFP procedures, and charts.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { AirportDataService } from '../services/AirportDataService';
import { ProcedureService } from '../services/ProcedureService';
import type {
  AirportDetail,
  Procedure,
  ChartMetadata,
} from '../types/airport';

export interface UseAirportDetailsState {
  detail: AirportDetail | null;
  procedures: Procedure[];
  charts: ChartMetadata[];
  loading: boolean;
  proceduresLoading: boolean;
  chartsLoading: boolean;
  error: string | null;
}

export interface UseAirportDetailsResult extends UseAirportDetailsState {
  fetchAirport: (identifier: string) => Promise<void>;
  clear: () => void;
}

export function useAirportDetails(
  initialIdentifier?: string,
): UseAirportDetailsResult {
  const [state, setState] = useState<UseAirportDetailsState>({
    detail: null,
    procedures: [],
    charts: [],
    loading: false,
    proceduresLoading: false,
    chartsLoading: false,
    error: null,
  });

  const airportServiceRef = useRef(new AirportDataService());
  const procedureServiceRef = useRef(new ProcedureService());

  const fetchAirport = useCallback(async (identifier: string) => {
    if (!identifier) return;
    const id = identifier.trim().toUpperCase();

    setState((s) => ({
      ...s,
      loading: true,
      proceduresLoading: true,
      chartsLoading: true,
      error: null,
      detail: null,
      procedures: [],
      charts: [],
    }));

    try {
      // Fetch airport detail first (fast, essential)
      const detail = await airportServiceRef.current.getAirportDetail(id);

      setState((s) => ({
        ...s,
        detail,
        loading: false,
        error: detail ? null : `Airport not found: ${id}`,
      }));

      if (!detail) {
        setState((s) => ({
          ...s,
          proceduresLoading: false,
          chartsLoading: false,
        }));
        return;
      }

      // Fetch procedures and charts in parallel (secondary data)
      const [procedures, charts] = await Promise.all([
        procedureServiceRef.current.getProcedures(detail.icao).finally(() => {
          /* keep promise clean */
        }),
        procedureServiceRef.current.getCharts(detail.icao).finally(() => {
          /* keep promise clean */
        }),
      ]);

      setState((s) => ({
        ...s,
        procedures: annotateProceduresWithCharts(procedures, charts),
        charts,
        proceduresLoading: false,
        chartsLoading: false,
      }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load airport';
      setState((s) => ({
        ...s,
        loading: false,
        proceduresLoading: false,
        chartsLoading: false,
        error: msg,
      }));
    }
  }, []);

  const clear = useCallback(() => {
    setState({
      detail: null,
      procedures: [],
      charts: [],
      loading: false,
      proceduresLoading: false,
      chartsLoading: false,
      error: null,
    });
  }, []);

  // Auto-fetch on initial identifier
  useEffect(() => {
    if (initialIdentifier) {
      void fetchAirport(initialIdentifier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIdentifier]);

  return { ...state, fetchAirport, clear };
}

/**
 * Cross-reference procedures with charts so we know which procedures have
 * published plates available.
 */
function annotateProceduresWithCharts(
  procedures: Procedure[],
  charts: ChartMetadata[],
): Procedure[] {
  const chartProcIds = new Set(
    charts.map((c) => c.procedureId).filter((id): id is string => Boolean(id)),
  );
  const chartNames = charts.map((c) => c.name.toUpperCase());

  return procedures.map((p) => {
    const hasMatchById = chartProcIds.has(p.identifier);
    const hasMatchByName = chartNames.some((n) =>
      n.includes(p.identifier.toUpperCase()),
    );
    return { ...p, hasChart: hasMatchById || hasMatchByName };
  });
}
